import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import React from 'react'
import { FormControl } from 'react-bootstrap-five'
import '../PromoCodes.scss'

const t = buildTranslate('promo_codes.create_edit.promo_code_section')

export type CodeType = 'single' | 'unique'

type PromoCodeSectionProps = {
  codeType: CodeType
  onCodeTypeChange: (type: CodeType) => void
  staticCode: string
  onStaticCodeChange: (code: string) => void
  codeCount: string
  onCodeCountChange: (count: string) => void
  staticCodeError?: string
  codeCountError?: string
}

const PromoCodeSection = ({
  codeType,
  onCodeTypeChange,
  staticCode,
  onStaticCodeChange,
  codeCount,
  onCodeCountChange,
  staticCodeError,
  codeCountError,
}: PromoCodeSectionProps) => {
  // Stable ids so the inputs above can link their aria-describedby to the
  // error spans below. Matches the pattern used for `data-testid`.
  const staticCodeErrorId = 'static-code-error'
  const codeCountErrorId = 'code-count-error'
  return (
    <div className="promo-section-card">
      <Text.Header4 className="mb-s">{t('title')}</Text.Header4>
      <Text.BodyText4 className="grey-70 d-block mb-m">
        {t('description')}
      </Text.BodyText4>

      <label
        className={`promo-selection-card${
          codeType === 'single' ? ' promo-selection-card--selected' : ''
        }`}
        data-testid="code-type-single"
      >
        <div className="d-flex align-items-start">
          <input
            type="radio"
            name="codeType"
            checked={codeType === 'single'}
            onChange={() => onCodeTypeChange('single')}
            className="mt-xs mr-m"
          />
          <div className="flex-grow-1">
            <Text.BodyText3 bold className="d-block">
              {t('single.title')}
            </Text.BodyText3>
            <Text.BodyText4 className="grey-70 d-block mb-m">
              {t('single.description')}
            </Text.BodyText4>
            {codeType === 'single' && (
              <div>
                <Text.BodyText5 bold className="mb-xs">
                  {t('single.code_label')}
                </Text.BodyText5>
                <FormControl
                  type="text"
                  placeholder={t('single.placeholder')}
                  value={staticCode}
                  onChange={e =>
                    onStaticCodeChange(
                      e.target.value.replace(/\s/g, '').toUpperCase()
                    )
                  }
                  data-testid="static-code-input"
                  className="promo-input-narrow"
                  aria-label={t('single.code_label')}
                  aria-invalid={Boolean(staticCodeError)}
                  aria-describedby={
                    staticCodeError ? staticCodeErrorId : undefined
                  }
                />
                {staticCodeError && (
                  <span
                    id={staticCodeErrorId}
                    role="alert"
                    className="text-danger error-message font-size-14 d-block mt-xs"
                    data-testid="static-code-error"
                  >
                    {staticCodeError}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </label>

      <label
        className={`promo-selection-card${
          codeType === 'unique' ? ' promo-selection-card--selected' : ''
        }`}
        data-testid="code-type-unique"
      >
        <div className="d-flex align-items-start">
          <input
            type="radio"
            name="codeType"
            checked={codeType === 'unique'}
            onChange={() => onCodeTypeChange('unique')}
            className="mt-xs mr-m"
          />
          <div className="flex-grow-1">
            <Text.BodyText3 bold className="d-block">
              {t('unique.title')}
            </Text.BodyText3>
            <Text.BodyText4 className="grey-70 d-block">
              {t('unique.description')}
            </Text.BodyText4>
            {codeType === 'unique' && (
              <div className="mt-m">
                <Text.BodyText5 bold className="mb-xs">
                  {t('unique.count_label')}
                </Text.BodyText5>
                <FormControl
                  type="number"
                  max={4000000}
                  value={codeCount}
                  onChange={e => onCodeCountChange(e.target.value)}
                  data-testid="code-count-input"
                  className="promo-input-narrow"
                  aria-label={t('unique.count_label')}
                  aria-invalid={Boolean(codeCountError)}
                  aria-describedby={
                    codeCountError ? codeCountErrorId : undefined
                  }
                />
                {codeCountError && (
                  <span
                    id={codeCountErrorId}
                    role="alert"
                    className="text-danger error-message font-size-14 d-block mt-xs"
                    data-testid="code-count-error"
                  >
                    {codeCountError}
                  </span>
                )}
                <div className="mt-xs" data-testid="generation-time-note">
                  <Text.BodyText5 className="grey-70 d-block">
                    {t('unique.generation_note')}
                  </Text.BodyText5>
                </div>
              </div>
            )}
          </div>
        </div>
      </label>
    </div>
  )
}

export default PromoCodeSection
